import { Injectable } from '@angular/core';
import { BehaviorSubject, Observable } from 'rxjs';

export type ThemeType = 'light-theme' | 'dark-theme' | 'motivatexr-theme';

export interface ThemeConfig {
  name: string;
  displayName: string;
  cssClass: ThemeType;
  icon: string;
}

@Injectable({
  providedIn: 'root'
})
export class ThemeService {
  private readonly STORAGE_KEY = 'app-theme';
  private readonly DEFAULT_THEME: ThemeType = 'dark-theme';

  private currentThemeSubject: BehaviorSubject<ThemeType>;
  public currentTheme$: Observable<ThemeType>;

  public readonly availableThemes: ThemeConfig[] = [
    {
      name: 'light',
      displayName: 'Light Theme',
      cssClass: 'light-theme',
      icon: 'light_mode'
    },
    {
      name: 'dark',
      displayName: 'Dark Theme',
      cssClass: 'dark-theme',
      icon: 'dark_mode'
    },
    {
      name: 'motivatexr',
      displayName: 'MotivateXR Theme',
      cssClass: 'motivatexr-theme',
      icon: 'mxr_mode'
    }
  ];

  constructor() {
    const savedTheme = this.loadThemeFromStorage();
    this.currentThemeSubject = new BehaviorSubject<ThemeType>(savedTheme);
    this.currentTheme$ = this.currentThemeSubject.asObservable();

    // Apply the initial theme
    this.applyTheme(savedTheme);
  }

  /**
   * Get the current active theme
   */
  getCurrentTheme(): ThemeType {
    return this.currentThemeSubject.value;
  }

  /**
   * Set a new theme
   */
  setTheme(theme: ThemeType): void {
    if (this.currentThemeSubject.value !== theme) {
      this.currentThemeSubject.next(theme);
      this.applyTheme(theme);
      this.saveThemeToStorage(theme);
    }
  }

  /**
   * Toggle between light and dark themes
   */
  toggleTheme(): void {
    const currentTheme = this.getCurrentTheme();
    const newTheme: ThemeType = currentTheme === 'light-theme' ? 'dark-theme' : 'light-theme';
    this.setTheme(newTheme);
  }

  /**
   * Get theme config by CSS class name
   */
  getThemeConfig(cssClass: ThemeType): ThemeConfig | undefined {
    return this.availableThemes.find(theme => theme.cssClass === cssClass);
  }

  /**
   * Apply theme to the document body
   */
  private applyTheme(theme: ThemeType): void {
    const body = document.body;

    // Remove all theme classes
    this.availableThemes.forEach(t => {
      body.classList.remove(t.cssClass);
    });

    // Add the new theme class
    body.classList.add(theme);
  }

  /**
   * Load theme from localStorage
   */
  private loadThemeFromStorage(): ThemeType {
    try {
      const savedTheme = localStorage.getItem(this.STORAGE_KEY);
      if (savedTheme && this.isValidTheme(savedTheme)) {
        return savedTheme as ThemeType;
      }
    } catch (error) {
      console.warn('Failed to load theme from storage:', error);
    }
    return this.DEFAULT_THEME;
  }

  /**
   * Save theme to localStorage
   */
  private saveThemeToStorage(theme: ThemeType): void {
    try {
      localStorage.setItem(this.STORAGE_KEY, theme);
    } catch (error) {
      console.warn('Failed to save theme to storage:', error);
    }
  }

  /**
   * Validate if a string is a valid theme
   */
  private isValidTheme(theme: string): boolean {
    return this.availableThemes.some(t => t.cssClass === theme);
  }

  /**
   * Check if dark theme is active
   */
  isDarkTheme(): boolean {
    return this.getCurrentTheme() === 'dark-theme';
  }

  /**
   * Check if light theme is active
   */
  isLightTheme(): boolean {
    return this.getCurrentTheme() === 'light-theme';
  }
}
